import { TextField } from '@mui/material';
import { Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AuthenticatedSessionContext } from '../../authenticatedSession/AuthenticatedSessionContext';
import type { AuthenticatedSession } from '../../authenticatedSession/authenticatedSession';
import {
  ExpenseEntryData,
  ExpenseEntryDocument,
} from '../../database/baseTypes';
import { useExpenseEntryAdd } from '../../database/hooks/expenseEntries';
import { textFieldStyles } from '../../muiStyles/textFieldStyles';
import DateTextField from '../DateTextField/DateTextField';
import MatterAutocomplete from '../MatterAutocomplete';
import { QuickEntryModal } from '../QuickEntryModal/QuickEntryModal';
import { defaultState, formReducer } from './reducer';
import {
  ymdFromLocalDate,
  ymdIsEqualTo,
  ymdToDate,
} from '@/database/yearMonthDayUtil';
import { deleteDoc, DocumentReference } from 'firebase/firestore';
import { validNumber } from '@/utils/numberFormatters';
import { auditingEventForExpenseEntryField } from '@/views/Expenses/ExpenseRow/auditingEventForExpenseEntryField';

const ExpenseEntryModal = ({
  closeOnSave = false,
  entry,
  fixedMatterId,
  onClose,
}: {
  closeOnSave?: boolean;
  entry?: ExpenseEntryDocument;
  fixedMatterId: string | undefined;
  onClose: (submitted?: boolean) => void;
}) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [state, dispatch] = useReducer(
    formReducer,
    defaultState(
      undefined,
      entry ? ymdToDate(entry.incurredYmd) : new Date(),
      entry ? validNumber(entry.amount) : undefined,
      entry?.description,
    ),
  );
  const startingInputRef = useRef<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (!isSaving) onClose(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element)?.className === 'modal-backdrop') {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClose]);

  useEffect(() => {
    startingInputRef.current?.focus();
  }, []);

  const addExpenseEntry = useExpenseEntryAdd();

  const handleSave = async (): Promise<boolean> => {
    if (!authSession) {
      return false;
    }

    let hasErrors = false;
    if (!state.date.value) {
      hasErrors = true;
      dispatch({ type: 'SET_DATE_ERROR', payload: 'Required' });
    }
    const ymd = ymdFromLocalDate(state.date.value!);

    if (!fixedMatterId && !state.matter.value) {
      hasErrors = true;
      dispatch({ type: 'SET_MATTER_ERROR', payload: 'Required' });
    }
    if (!state.description.value) {
      hasErrors = true;
      dispatch({ type: 'SET_DESCRIPTION_ERROR', payload: 'Required' });
    }
    const amount = state.amount.value;
    if (amount === undefined || amount === '') {
      hasErrors = true;
      dispatch({ type: 'SET_AMOUNT_ERROR', payload: 'Required' });
    }

    if (hasErrors) {
      notifications.show({
        title: 'Missing required fields',
        message: 'Highlighted fields are required',
        color: 'red.4',
        autoClose: 6000,
      });
      return false;
    }

    const newExpenseEntry: ExpenseEntryData = {
      incurredYmd: ymd!,
      amount: `${amount}`,
      matter: fixedMatterId ?? state.matter.value!.key,
      description: state.description.value!,
      user: entry?.user ?? authSession.userIds.firestore,
      createdTimestamp: new Date(),
      createdBy: authSession.userIds.firestore,
    };
    if (entry) {
      try {
        setIsSaving(true);
        const updateEvents = draftExpenseEntryEditEvents({
          authSession,
          entry,
          newExpenseEntry,
        });
        const data: Partial<ExpenseEntryData> = {
          amount: newExpenseEntry.amount,
          incurredYmd: newExpenseEntry.incurredYmd,
          description: newExpenseEntry.description,
          user: newExpenseEntry.user,
        };
        if (updateEvents.length > 0) {
          data.events = [...(entry.events ?? []), ...updateEvents];
        }
        await authSession.db.updateExpenseEntry(entry.id, data);
        notifications.show({
          title: 'Expense entry updated',
          message: 'The expense entry was updated.',
          color: 'teal',
        });
        return true;
      } finally {
        setIsSaving(false);
      }
    }
    const ref = await addExpenseEntry(newExpenseEntry);
    const successNotificationId = `new-expense-entry-${ref?.id ?? Date.now()}`;

    notifications.show({
      id: successNotificationId,
      title: 'Expense created 💵',
      message: (
        <Group justify='space-between' wrap='nowrap'>
          <span>New expense created successfully</span>
          <Button
            variant='white'
            color='dark'
            size='compact-xs'
            onClick={async () => {
              notifications.hide(successNotificationId);
              await handleUndo(ref);
            }}
          >
            Undo
          </Button>
        </Group>
      ),
      color: 'blue.4',
      autoClose: 6000,
    });

    flushSync(() => {
      dispatch({ type: 'CLEAR_FORM', payload: { matter: state.matter.value } });
    });

    startingInputRef.current?.focus();
    return true;
  };

  const handleUndo = async (
    refToDelete: DocumentReference<ExpenseEntryData> | undefined,
  ) => {
    if (!refToDelete) {
      return;
    }
    await deleteDoc(refToDelete);
    notifications.show({
      title: 'Undo successful',
      message: 'Expense entry has been removed',
      color: 'blue.4',
      autoClose: 1500,
    });
  };

  return (
    <QuickEntryModal
      title={entry ? 'Edit Expense Entry' : 'New Expense Entry'}
      saveButtonDisabled={isSaving}
      onClose={handleClose}
      onSave={async () => {
        const hasSaved = await handleSave();
        if (hasSaved && (closeOnSave || entry)) onClose(true);
      }}
    >
      {!fixedMatterId && (
        <MatterAutocomplete
          firmDb={authSession?.db}
          error={!!state.matter.error}
          value={state.matter.value}
          inputRef={startingInputRef}
          onSelect={(matter) => {
            dispatch({ type: 'SET_MATTER_VAL', payload: matter });
          }}
        />
      )}
      <DateTextField
        value={state.date.value ?? null}
        error={!!state.date.error}
        required={true}
        onChange={(value) => dispatch({ type: 'SET_DATE_VAL', payload: value })}
      />
      <TextField
        sx={textFieldStyles}
        label='$ Amount'
        variant='outlined'
        type='number'
        onChange={(e) =>
          dispatch({ type: 'SET_AMOUNT_VAL', payload: e.target.value })
        }
        value={state.amount.value ?? ''}
        error={!!state.amount.error}
        required
      />
      <TextField
        sx={textFieldStyles}
        label='Description'
        multiline
        rows={4}
        error={!!state.description.error}
        value={state.description.value ?? ''}
        required={true}
        onChange={(e) =>
          dispatch({ type: 'SET_DESCRIPTION_VAL', payload: e.target.value })
        }
      />
    </QuickEntryModal>
  );
};

export default ExpenseEntryModal;

const draftExpenseEntryEditEvents = ({
  authSession,
  entry,
  newExpenseEntry,
}: {
  authSession: AuthenticatedSession;
  entry: ExpenseEntryDocument;
  newExpenseEntry: ExpenseEntryData;
}) => [
  ...(!ymdIsEqualTo({
    ymd: entry.incurredYmd,
    other: newExpenseEntry.incurredYmd,
  })
    ? [
        auditingEventForExpenseEntryField({
          field: 'incurredYmd',
          previousValue: entry.incurredYmd,
          value: newExpenseEntry.incurredYmd,
          authSession,
        }),
      ]
    : []),
  ...(validNumber(entry.amount) !== validNumber(newExpenseEntry.amount)
    ? [
        auditingEventForExpenseEntryField({
          field: 'amount',
          previousValue: entry.amount,
          value: newExpenseEntry.amount,
          authSession,
        }),
      ]
    : []),
  ...(entry.description !== newExpenseEntry.description
    ? [
        auditingEventForExpenseEntryField({
          field: 'description',
          previousValue: entry.description,
          value: newExpenseEntry.description,
          authSession,
        }),
      ]
    : []),
];
